import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Carate, Period } from 'lumen/models';
import {
  formatNamesLabel,
  groupByMinLos,
} from 'lumen/utils/pricing-assistant/rate-group-restriction-utils';
import { formatMinLosValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './pricing-assistant-table-tooltip.module.css';

interface RoomRow {
  formattedValue: string;
  label: string;
}

interface HotelPricingAssistantTableTooltipsTooltipRoomsMonthSignature {
  Args: {
    period: Period;
    rooms: Carate[];
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipRoomsMonth extends Component<HotelPricingAssistantTableTooltipsTooltipRoomsMonthSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  get formattedDate(): string {
    return this.args.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  /* Rooms have no overrides or pushes, so they group by value alone, in table
     order. */
  get rows(): RoomRow[] {
    const entries = this.args.rooms.map(({ pmsRoomtype, roomName }) => ({
      hasOverride: false,
      isLoading: false,
      roomName,
      value: this.args.period.roomMinLos(pmsRoomtype),
    }));

    return groupByMinLos(entries).map((row) => ({
      formattedValue: formatMinLosValue({ intl: this.intl, value: row.value }),
      label: formatNamesLabel(
        row.items.map(({ roomName }) => roomName),
        this.intl,
      ),
    }));
  }

  <template>
    <EmberTooltip
      @side="bottom"
      @tooltipClass="base-tooltip {{styles.container}}"
    >
      <div
        class="{{styles.header}} p-400"
        data-test-id="tooltip-rooms-month-header"
      >
        <h4>{{this.formattedDate}}</h4>
      </div>
      <table
        class="default-table {{styles.table}} {{styles.wrapping-names-table}}"
      >
        <tbody>
          {{#each this.rows as |row|}}
            <tr data-test-id="tooltip-rooms-month-row">
              <td class="{{styles.table-row-cell}} text-200--regular">
                <p class="text-200--regular {{styles.wrapping-cell}}">
                  {{row.label}}
                </p>
                <p class="text-200--regular {{styles.fixed-cell}}">
                  {{row.formattedValue}}
                </p>
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </EmberTooltip>
  </template>
}
